/**
 * This file hits many back-end endpoints that require snake_case keys. Instead of
 * running eslint-disable-line many times, disabling the checks for the entire file:
 */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable camelcase */
import axios, { AxiosError } from 'axios'
import { Lead } from '../types'
import { PhoneNumberData } from 'phone-number'
import { Bill } from '../types'
import {
    getCookieValue,
    setCookie,
    removeCookie
} from './Cookies'
import { LeadParams } from '../types'
import * as WebEyez from './WebEyez'
import { parsePhoneNumber } from 'libphonenumber-js'
import { ValidationError } from './Validations'

const sessionTokenCookieName = 'pdc_lead_session_token'

interface CreateAccountStatusReport {
    status: 'not_started' | 'in_progress' | 'complete' | 'failed';
    create_account_errors: CreateAccountErrorJSON[] // eslint-disable-line @typescript-eslint/naming-convention
}

interface CreateAccountErrorJSON {
    error_type: string; // eslint-disable-line @typescript-eslint/naming-convention
    internal_details: string; // eslint-disable-line @typescript-eslint/naming-convention
    user_facing_description: string; // eslint-disable-line @typescript-eslint/naming-convention
    error_code: number | null; // eslint-disable-line @typescript-eslint/naming-convention
}

/**
 * Represents an error that occurs during the account creation process.
 * This error class will contain all the details specific to what create-account-async API
 */
export class CreateAccountError extends Error {
    createAccountErrors: CreateAccountErrorJSON[];

    /**
     * @param {CreateAccountStatusReport} statusRepport the status report received from get-create-account-status API
     */
    constructor (statusRepport: CreateAccountStatusReport) {
        super(statusRepport?.create_account_errors[0]?.user_facing_description || 'An error occured while setting up your account, your card has not been charged.')
        this.createAccountErrors = statusRepport.create_account_errors
    }
}

/**
 *
 */
export class CartEmptyError extends Error {
}

/**
 * Private helper function that handles common tasks like handling errors
 * and HttpRedirectRequests across all API requests.
 * @param url url to send the request to
 * @param params request body parameters
 */
const callAPI = (url : string, params : unknown = {}, method = 'post') => {
    // If no session token is provided in the params, check for a session token cookie.
    // Note: The session token from the params is prefered so that a session can be
    // overwritten without clearing cookies manually. E.g. When a sales team member sends
    // someone a URL that has a session token in it, the session that already existed in
    // the user's browser (if there was one) should be replaced by the session that the
    // sales team member just shared with them.
    if (!params.session_token) {
        const sessionTokenFromCookie = getCookieValue(sessionTokenCookieName)
        if (sessionTokenFromCookie) {
            params.session_token = sessionTokenFromCookie
        }
    }

    return axios[method](url, params).then(response => {
        const { data } = response

        const handleError = (error: Error) => {
            WebEyez.reportAPICall({
                apiURL: url,
                requestPayload: params,
                responseText: `${JSON.stringify(data)}`,
                textStatus: response.statusText,
                statusCode: response.status,
                isSuccess: false,
                errorMessage: error.message
            })

            throw error
        }

        if (data?.errorType === 'HttpRedirectRequest') {
            handleError(new Error(data?.errorMessage || 'Unexpected redirect response'))
        }

        let error = null
        if (typeof data.errors === 'object') { // ValidationErrors are serialized from the backend like this
            // TODO: if the server sends error info in this format:
            // { errors: { field_name: error_for_that_field } }
            // This can be used if needed in the future to extract the name(s) of the fields associated with the error
            error = new ValidationError(Object.values(data.errors)[0], data.error_code)
        } else if (data.status === 'error') {
            error = new Error(data.details)
        } else {
            const errorMessage = data.error?.errors?.payment_decline || data.errorMessage || data.error?.message || data.error
            if (errorMessage) {
                error = new Error(errorMessage)
            }
        }

        if (error) {
            handleError(error)
        }

        WebEyez.reportAPICall({
            apiURL: url,
            requestPayload: params,
            responseText: `${JSON.stringify(data)}`,
            textStatus: response.statusText,
            statusCode: response.status,
            isSuccess: true
        })

        return response
    })
}

/**
 * Fetches lead data representing the user (as a prospective customer or lead), this will register a new lead if necessary,
 * and set the lead session token cookie so the lead can be referenced in the future.
 */
export const initLead = async (): Promise<null | LeadParams> => {
    // Get URL query params to pass to back end
    const params = Object.fromEntries(new URLSearchParams(window.location.search))

    // Tell the signup init endpoint to return JSON instead of HTML (needed because that
    // endpoint was originally a front-end endpoint that returned HTML)
    params.format = 'json'

    const requestUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up`

    return callAPI(requestUrl, params).then(response => {
        setCookie(sessionTokenCookieName, response.data.session_token, { expires: 30 }) // 30 days
        return response.data
    })
}

/**
 * Redirects the user back to the correct pricing page
 */
export const redirectToPricingPage = (): void => {
    const isClassicSignUp = getCookieValue('is_classic_signup')?.toLowerCase() === 'true'

    const url = isClassicSignUp ? process.env.REACT_APP_CLASSIC_PRICING_PAGE_URL : process.env.REACT_APP_USERS_PRICING_PAGE_URL

    const pricingPageURL = new URL(url)
    const searchParams = new URLSearchParams({ destination_url: window.location.host })
    pricingPageURL.search = searchParams.toString()

    console.log(`Redirecting user to pricing page: ${pricingPageURL}`)
    window.location = pricingPageURL?.toString()
}

interface ReserveNumberPayload {
    number_picker_session_token: string;
    number_e164: string;
    number_mask?: string;
}

/**
 * Reserves a phone number, adding it to the user's/lead's cart
 * In some cases this may fail if the number has already recently been taken
 */
export const reserveNumber = async (phoneNumber: PhoneNumberData) => {
    const payload: ReserveNumberPayload = {
        number_picker_session_token: getCookieValue('pdc_number_picker_session_token'),
        number_e164: phoneNumber.e164
    }
    if (phoneNumber.mask) {
        payload.number_mask = phoneNumber.mask
    }
    return callAPI(`https://${process.env.REACT_APP_API_HOST}/sign-up/steps/pick-a-number/postback`, payload)
}

/**
 * Get pricing summary details. Hits one endpoint to trigger pricing then polls another
 * endpoint until pricing has finished calculating.
 */
export const getSummary = async (couponCode : string, paymentPeriod: number): Promise<Bill> => {
    const initUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/view-summary-and-terms/get-summary-async`
    const pollingUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/view-summary-and-terms/get-summary-status`

    // Trigger pricing calculation
    await callAPI(initUrl, {
        coupon_code: couponCode,
        payment_period: paymentPeriod
    })

    const pollInterval = 1000

    const waitThenCheck = async () => new Promise(resolve => setTimeout(resolve, pollInterval))

    // Keep checking status untill bill is ready
    while (true) {
        const getSummaryError = (errorMessage: string) => {
            if (errorMessage === 'cart_empty') {
                return new CartEmptyError('Your number reservation expired, please pick a number.')
            } else {
                return new Error('A problem occured while loading your order summary')
            }
        }
        const statusResponse = await callAPI(pollingUrl).catch((error: Error) => {
            throw getSummaryError(error.message)
        })

        const status = statusResponse?.data?.status
        console.debug(`Checking order summary, received status: ${status}`)

        switch (status) {
                case 'not_started':
                case 'in_progress':
                    await waitThenCheck()
                    break
                case 'completed':
                    return statusResponse.data.bill
                case 'failed':
                    throw getSummaryError(statusResponse?.data.error)
                default:
                    console.error(`get-summary-status received an invalid status: "${status}"`)
                    await waitThenCheck()
        }
    }
}

/**
 * Reserves a temporary number for the lead (or retrieves it if one is already reserved)
 */
export async function getTransferNumber () {
    const requestUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/transfer-my-number`
    return callAPI(requestUrl)
}

/**
 * Checks if a zip code (or postal code) is valid.
 */
export async function checkZipCode (zipCode: string) {
    const requestUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/enter-zip-code/check-zip-code` // TODO create API dedicated to updating lead
    return callAPI(requestUrl, {
        zipcode: zipCode
    })
}

/**
 * Saves various lead information including things like name, email, business address, etc.
 */
export async function saveLead (lead: Lead, mfaReturnURL?: string | null) {
    lead.gcid = getCookieValue('_ga')

    const params = {
        ...lead
    }

    if (lead.personal_phone_number) {
        // When saving personal_phone_number send it in E.164 format ("+1XXXXXXXXXX")
        params.personal_phone_number = parsePhoneNumber(lead.personal_phone_number, 'US').number
    }

    if (mfaReturnURL) {
        params.mfa_return_url = mfaReturnURL // This param when present will instruct the backend to initiate the setup of multi-factor authentication (MFA)
    }

    const requestUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/enter-zip-code` // TODO create API dedicated to updating lead
    return callAPI(requestUrl, params)
}

/**
 * Saves the token received locally from the google recaptcha human verification library on the backend,
 * and verifies the authenticity of the token.
 */
export async function verifyRecaptcha (recaptchaResponseToken : string) {
    const requestUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/create-credentials/verify-recaptcha`
    return callAPI(requestUrl, {
        g_recaptcha_response_token: recaptchaResponseToken
    })
}

/**
 * Saves the Stripe payment token on the backend, so the payment information (card details) can then be used
 * to charge the customer
 */
export async function createPaymentToken (stripePaymentToken : string) {
    const requestUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/enter-credit-card/create-payment-token`
    return callAPI(requestUrl, {
        stripe_payment_token: stripePaymentToken
    })
}

interface CreateAccountOptions {
    receiveSMS: boolean;
    receiveSMSOffers: boolean;
}

/**
 * Create the user's account. Hits one endpoint to trigger account creation then polls
 * another endpoint until account creation has completed or failed.
 */
export async function createAccount (options: CreateAccountOptions) {
    const initUrl = `https://${process.env.REACT_APP_API_HOST}/sign-up/steps/login/create-account-async`
    const pollingUrl = `https://${process.env.REACT_APP_SERVICES_HOST}/accounts/get-create-account-status`

    // Trigger account creation
    await callAPI(initUrl, {
        receive_sms: options.receiveSMS,
        receive_sms_offers: options.receiveSMSOffers
    })

    const pollInterval = 1000

    // Keep polling untill account creation status is completed for failed
    while (true) {
        const response = await callAPI(pollingUrl).catch((error: AxiosError) => {
            if (!error.status && error.message.includes('Network Error')) {
                throw new Error('Network Error: please check your internet connection. Your order has still been submitted and will continue processing')
            }
        })
        const statusReport = response?.data
        const status = statusReport?.status
        console.debug(`Checking account creation process, received status: ${status}`)

        switch (status) {
                case 'complete':
                    removeCookie('pdc_lead_session_token')
                    return statusReport.created_account_details
                case 'failed':
                    throw new CreateAccountError(statusReport)
                case 'not_started':
                case 'in_progress':
                    break
                default:
                    console.warn(`Invalid status received from API ${pollingUrl}: status: ${status}`)
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval))
    }
}
